import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from './config';
import { createMonitorSocket } from '../socket/socket';
import '../css/Monitor.css';

const toArray = value => (
  Array.isArray(value)
    ? value
    : Array.isArray(value?.data)
      ? value.data
      : []
);

const fetchJson = async path => {
  const response = await fetch(`${API_BASE}${path}`);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || data.error || `HTTP ${response.status}`);
  }

  return data;
};

const matchTimestamp = match => {
  const timestamp = new Date(match?.created_at || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const endedMatchTimestamp = match => {
  const timestamp = new Date(
    match?.ended_at ||
    match?.end_time ||
    match?.updated_at ||
    match?.created_at ||
    0
  ).getTime();

  return Number.isFinite(timestamp) ? timestamp : 0;
};

const playerColorStyle = player => {
  const source = String(player?.user_id ?? player?.name ?? 'player');
  const hash = [...source].reduce(
    (total, character) => ((total * 31) + character.charCodeAt(0)) >>> 0,
    0
  );
  const hue = Math.round((hash * 137.508) % 360);

  return {
    '--monitor-player-ink': `hsl(${hue} 62% 31%)`,
    '--monitor-player-bg': `hsl(${hue} 86% 95%)`,
    '--monitor-player-border': `hsl(${hue} 62% 82%)`
  };
};

function PlayerNameButton({ player, onClick }) {
  return (
    <button
      type="button"
      className="monitor-player-link"
      style={playerColorStyle(player)}
      onClick={() => onClick(player)}
      title={`ดูประวัติการเล่นของ ${player.name || 'ผู้เล่น'}`}
    >
      <span>{player.name || 'ไม่ระบุชื่อ'}</span>
    </button>
  );
}

function MatchCard({ match, tone, onPlayerClick }) {
  const team1 = match.players.filter(player => Number(player.team) === 1);
  const team2 = match.players.filter(player => Number(player.team) === 2);

  const courtLabel =
    match.court_no
      ? `Court ${match.court_no}`
      : '-';

  return (
    <article className={`monitor-match-card ${tone}`}>
      <header>
        <span>
          Match #{match.groupMatchNo || match.matchId}
        </span>

        <strong>
          {tone === 'playing'
            ? courtLabel
            : tone === 'ended'
              ? `✅ จบแล้ว • ${courtLabel}`
              : 'รอสนาม'}
        </strong>
      </header>

      <div className="monitor-match-teams">
        <div>
          <small>TEAM 1</small>

          {team1.map(player => (
            <PlayerNameButton
              key={player.user_id}
              player={player}
              onClick={onPlayerClick}
            />
          ))}
        </div>

        <b>VS</b>

        <div>
          <small>TEAM 2</small>

          {team2.map(player => (
            <PlayerNameButton
              key={player.user_id}
              player={player}
              onClick={onPlayerClick}
            />
          ))}
        </div>
      </div>
    </article>
  );
}

function MatchBoard({ matches, onPlayerClick }) {
  const playingMatches = useMemo(
    () => matches
      .filter(
        match =>
          String(match.status).toLowerCase() === 'playing'
      )
      .sort(
        (left, right) =>
          Number(left.court_no || 999) -
          Number(right.court_no || 999)
      ),
    [matches]
  );

  const queueMatches = useMemo(
    () => matches
      .filter(match =>
        [
          'waiting',
          'waiting_queue'
        ].includes(
          String(match.status).toLowerCase()
        )
      )
      .sort(
        (left, right) =>
          Number(left.queue_no || 999999) -
            Number(right.queue_no || 999999) ||
          matchTimestamp(left) -
            matchTimestamp(right) ||
          Number(left.matchId) -
            Number(right.matchId)
      ),
    [matches]
  );

  // ============================================
  // MATCH ที่จบแล้ว
  // ============================================

  const endedMatches = useMemo(
    () => matches
      .filter(match =>
        [
          'ended',
          'end',
          'completed',
          'finished'
        ].includes(
          String(match.status || '')
            .trim()
            .toLowerCase()
        )
      )
      // จบล่าสุดอยู่บนสุด
      .sort(
        (left, right) =>
          endedMatchTimestamp(right) -
            endedMatchTimestamp(left) ||
          Number(right.matchId || 0) -
            Number(left.matchId || 0)
      ),
    [matches]
  );

  return (
    <>
      {/* ============================= */}
      {/* QUEUE + PLAY */}
      {/* ============================= */}

      <section
        className="monitor-match-board"
        aria-label="Match Play และ Match Queue"
      >
        {/* MATCH QUEUE */}

        <div className="monitor-match-column">
          <header className="monitor-match-heading queue-heading">
            <span>
              <strong>Match Queue</strong>
              <small>
                Match ที่กำลังรอลงสนามตามลำดับคิว
              </small>
            </span>

            <b>{queueMatches.length} Match</b>
          </header>

          <div className="monitor-match-list">
            {queueMatches.length > 0
              ? queueMatches.map(match => (
                  <MatchCard
                    key={match.matchId}
                    match={match}
                    tone="waiting"
                    onPlayerClick={onPlayerClick}
                  />
                ))
              : (
                <div className="monitor-empty">
                  ยังไม่มี Match ในคิว
                </div>
              )}
          </div>
        </div>

        {/* MATCH PLAY */}

        <div className="monitor-match-column">
          <header className="monitor-match-heading play-heading">
            <span>
              <strong>Match Play</strong>
              <small>
                Match ที่กำลังแข่งขันอยู่
              </small>
            </span>

            <b>{playingMatches.length} Match</b>
          </header>

          <div className="monitor-match-list">
            {playingMatches.length > 0
              ? playingMatches.map(match => (
                  <MatchCard
                    key={match.matchId}
                    match={match}
                    tone="playing"
                    onPlayerClick={onPlayerClick}
                  />
                ))
              : (
                <div className="monitor-empty">
                  ยังไม่มี Match ที่กำลังเล่น
                </div>
              )}
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* MATCH ENDED */}
      {/* ============================= */}

      <section className="monitor-ended-section">
        <header className="monitor-match-heading ended-heading">
          <span>
            <strong>✅ Match จบแล้ว</strong>
            <small>
              ประวัติ Match ที่เล่นจบแล้ว
            </small>
          </span>

          <b>{endedMatches.length} Match</b>
        </header>

        <div className="monitor-ended-list">
          {endedMatches.length > 0
            ? endedMatches.map(match => (
                <MatchCard
                  key={match.matchId}
                  match={match}
                  tone="ended"
                  onPlayerClick={onPlayerClick}
                />
              ))
            : (
              <div className="monitor-empty">
                ยังไม่มี Match ที่จบแล้ว
              </div>
            )}
        </div>
      </section>
    </>
  );
}

function Monitor() {
  const { groupId: routeGroupId } = useParams();
  const navigate = useNavigate();
  const selectedGroupId = Number(routeGroupId) || null;
  const [groups, setGroups] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const requestVersionRef = useRef(0);

  const loadGroups = useCallback(async () => {
    const data = await fetchJson('/api/groups');
    const nextGroups = toArray(data);
    setGroups(nextGroups);
    return nextGroups;
  }, []);

  const loadMatches = useCallback(async ({ silent = false } = {}) => {
    if (!selectedGroupId) {
      setMatches([]);
      setLoading(false);
      return;
    }

    const requestVersion = ++requestVersionRef.current;
    if (silent) setRefreshing(true);
    else setLoading(true);

    try {
      const data = await fetchJson(`/api/game-match/group/${selectedGroupId}`);
      if (requestVersion !== requestVersionRef.current) return;
      setMatches(toArray(data));
      setError('');
    } catch (loadError) {
      if (requestVersion !== requestVersionRef.current) return;
      setError(loadError.message || 'โหลด Match Monitor ไม่สำเร็จ');
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [selectedGroupId]);

  useEffect(() => {
    loadGroups().catch(loadError => {
      setError(loadError.message || 'โหลดรายการ Group ไม่สำเร็จ');
      setLoading(false);
    });
  }, [loadGroups]);

  useEffect(() => {
    if (groups.length === 0) return;
    const selectedExists = groups.some(
      group => Number(group.id) === Number(selectedGroupId)
    );
    if (!selectedExists) {
      navigate(`/monitor/${groups[0].id}`, { replace: true });
    }
  }, [groups, navigate, selectedGroupId]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  useEffect(() => {
    if (!selectedGroupId) return undefined;
    const intervalId = window.setInterval(
      () => loadMatches({ silent: true }),
      30000
    );
    return () => window.clearInterval(intervalId);
  }, [loadMatches, selectedGroupId]);

  useEffect(() => {
    if (!selectedGroupId) return undefined;
    const monitorSocket = createMonitorSocket();
    const joinGroup = () => monitorSocket.emit('join-group', selectedGroupId);
    const refreshMatches = (payload = {}) => {
      const eventGroupId = payload.groupId ?? payload.group_id;
      if (eventGroupId && Number(eventGroupId) !== selectedGroupId) return;
      loadMatches({ silent: true });
    };
    const matchEvents = [
      'match-generated',
      'match-started',
      'match-ended',
      'match-updated',
      'queue-updated',
      'match-cancelled',
      'court-status-updated'
    ];

    monitorSocket.on('connect', joinGroup);
    matchEvents.forEach(eventName => monitorSocket.on(eventName, refreshMatches));
    if (monitorSocket.connected) joinGroup();

    return () => {
      monitorSocket.off('connect', joinGroup);
      matchEvents.forEach(eventName => monitorSocket.off(eventName, refreshMatches));
      monitorSocket.disconnect();
    };
  }, [loadMatches, selectedGroupId]);

  const selectedGroup = groups.find(
    group => Number(group.id) === Number(selectedGroupId)
  );

  const handleGroupChange = event => {
    const nextGroupId = Number(event.target.value);
    if (nextGroupId) navigate(`/monitor/${nextGroupId}`);
  };

  const openPlayerHistory = player => {
    if (!player?.user_id || !selectedGroupId) return;
    navigate(`/player-history/${selectedGroupId}/${player.user_id}`);
  };

  return (
    <main className="monitor-page monitor-match-only">
      <header className="monitor-hero">
        <div className="monitor-brand">
          <span className="monitor-live-dot" aria-hidden="true" />
          <div>
            <span className="monitor-eyebrow">LIVE MATCH MONITOR</span>
            <h1>🏸 {selectedGroup?.Group_Name || 'Match Monitor'}</h1>
            <p>แสดงเฉพาะ Match Play และ Match Queue แบบเรียลไทม์</p>
          </div>
        </div>

        <div className="monitor-toolbar">
          <label htmlFor="monitor-group-select">
            <span>เลือก Group</span>
            <select
              id="monitor-group-select"
              value={selectedGroupId || ''}
              onChange={handleGroupChange}
              disabled={groups.length === 0}
            >
              {groups.length === 0 && <option value="">ไม่พบ Group</option>}
              {groups.map(group => (
                <option value={group.id} key={group.id}>
                  {group.Group_Name} ({group.Group_Status || 'OPEN'})
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="monitor-refresh-button"
            disabled={!selectedGroupId || refreshing}
            onClick={() => loadMatches({ silent: true })}
          >
            {refreshing ? '⏳ กำลังอัปเดต' : '↻ อัปเดตข้อมูล'}
          </button>
        </div>
      </header>

      {error && <div className="monitor-error" role="alert">❌ {error}</div>}

      {loading ? (
        <div className="monitor-loading">กำลังโหลด Match Monitor...</div>
      ) : (
        <MatchBoard matches={matches} onPlayerClick={openPlayerHistory} />
      )}
    </main>
  );
}

export default Monitor;
